import JSBI from 'jsbi';
import invariant from 'tiny-invariant';
import { Address } from '@ton/core';
import { NoTickDataProvider, TickDataProvider } from './tickDataProvider';
import { BigintIsh } from '../types/BigIntish';
import { Jetton } from './Jetton';
import { Tick, TickConstructorArgs } from './tick';
import { TickMath } from '../utils/tickMath';
import { TickListDataProvider } from './tickListDataProvider';
import { ADDRESS_ZERO, NEGATIVE_ONE, ONE, Q192, ZERO } from '../constants';
import { JettonAmount } from './JettonAmount';
import { SwapMath } from '../utils/swapMath';
import { LiquidityMath } from '../utils/liquidityMath';
import { Price } from './Price';
import { computePoolAddress } from '../functions/computePoolAddress';

interface StepComputations {
  sqrtPriceStartX96: JSBI;
  tickNext: number;
  initialized: boolean;
  sqrtPriceNextX96: JSBI;
  amountIn: JSBI;
  amountOut: JSBI;
  feeAmount: JSBI;
}

/**
 * By default, pools will not allow operations that require ticks.
 */
const NO_TICK_DATA_PROVIDER_DEFAULT = new NoTickDataProvider();

/**
 * Represents a V3 pool
 */
export class Pool {
  public readonly jetton0: Jetton;

  public readonly jetton1: Jetton;

  public readonly fee: number;

  public readonly sqrtRatioX96: JSBI;

  public readonly liquidity: JSBI;

  public readonly tickCurrent: number;

  public readonly tickDataProvider: TickDataProvider;

  public readonly _tickSpacing: number;

  /**
   * Jetton Wallet addreses attached to the Router
   */
  public readonly jetton0_wallet?: string;

  public readonly jetton1_wallet?: string;

  /**
   * Construct a pool
   * @param tokenA One of the tokens in the pool
   * @param tokenB The other token in the pool
   * @param fee The fee in hundredths of a bips of the input amount of every swap that is collected by the pool
   * @param sqrtRatioX96 The sqrt of the current ratio of amounts of token1 to token0
   * @param liquidity The current value of in range liquidity
   * @param tickCurrent The current tick of the pool
   * @param ticks The current state of the pool ticks or a data provider that can return tick data
   * @param _tickSpacing The spacing between ticks
   */
  public constructor(
    jettonA: Jetton,
    jettonB: Jetton,
    fee: number,
    sqrtRatioX96: BigintIsh,
    liquidity: BigintIsh,
    tickCurrent: number,
    _tickSpacing: number,
    ticks:
      | TickDataProvider
      | (Tick | TickConstructorArgs)[] = NO_TICK_DATA_PROVIDER_DEFAULT,
    jetton0_wallet?: string,
    jetton1_wallet?: string
  ) {
    invariant(Number.isInteger(fee) && fee < 1_000_000, 'FEE');

    const tickCurrentSqrtRatioX96 = TickMath.getSqrtRatioAtTick(tickCurrent);
    const nextTickSqrtRatioX96 = TickMath.getSqrtRatioAtTick(tickCurrent + 1);
    invariant(
      JSBI.greaterThanOrEqual(
        JSBI.BigInt(sqrtRatioX96),
        tickCurrentSqrtRatioX96
      ) &&
        JSBI.lessThanOrEqual(JSBI.BigInt(sqrtRatioX96), nextTickSqrtRatioX96),
      'PRICE_BOUNDS'
    );

    // always create a copy of the list since we want the pool's tick list to be immutable
    [this.jetton0, this.jetton1] = [jettonA, jettonB];
    [this.jetton0_wallet, this.jetton1_wallet] = [
      jetton0_wallet,
      jetton1_wallet,
    ];
    this.fee = fee;
    this.sqrtRatioX96 = JSBI.BigInt(sqrtRatioX96);
    this.liquidity = JSBI.BigInt(liquidity);
    this.tickCurrent = tickCurrent;
    this.tickDataProvider = Array.isArray(ticks)
      ? new TickListDataProvider(ticks, _tickSpacing)
      : ticks;
    this._tickSpacing = _tickSpacing;
  }

  private _jetton0Price?: Price<Jetton, Jetton>;

  /**
   * Returns the current mid price of the pool in terms of token0, i.e. the ratio of token1 over token0
   */
  public get jetton0Price(): Price<Jetton, Jetton> {
    return (
      this._jetton0Price ??
      (this._jetton0Price = new Price(
        this.jetton0,
        this.jetton1,
        Q192,
        JSBI.multiply(this.sqrtRatioX96, this.sqrtRatioX96)
      ))
    );
  }

  private _jetton1Price?: Price<Jetton, Jetton>;

  /**
   * Returns the current mid price of the pool in terms of token1, i.e. the ratio of token0 over token1
   */
  public get jetton1Price(): Price<Jetton, Jetton> {
    return (
      this._jetton1Price ??
      (this._jetton1Price = new Price(
        this.jetton1,
        this.jetton0,
        JSBI.multiply(this.sqrtRatioX96, this.sqrtRatioX96),
        Q192
      ))
    );
  }

  public get tickSpacing(): number {
    return this._tickSpacing;
  }

  public static getAddress(
    token0_wallet: string | undefined,
    token1_wallet: string | undefined
  ) {
    if (!token0_wallet || !token1_wallet) return Address.parse(ADDRESS_ZERO);
    return computePoolAddress(
      Address.parse(token0_wallet),
      Address.parse(token1_wallet)
    );
  }

  /**
   * Returns true if the token is either token0 or token1
   * @param token The token to check
   * @returns True if token is either token0 or token
   */
  public involvesToken(jetton: Jetton): boolean {
    return jetton.equals(this.jetton0) || jetton.equals(this.jetton1);
  }

  /**
   * Return the price of the given token in terms of the other token in the pool.
   * @param token The token to return price of
   * @returns The price of the given token, in terms of the other.
   */
  public priceOf(jetton: Jetton): Price<Jetton, Jetton> {
    invariant(this.involvesToken(jetton), 'TOKEN');
    return jetton.equals(this.jetton0) ? this.jetton0Price : this.jetton1Price;
  }

  /**
   * Given an input amount of a token, return the computed output amount, and a pool with state updated after the trade
   * @param inputAmount The input amount for which to quote the output amount
   * @param sqrtPriceLimitX96 The Q64.96 sqrt price limit
   * @returns The output amount and the pool with updated state
   */
  public async getOutputAmount(
    inputAmount: JettonAmount<Jetton>,
    sqrtPriceLimitX96?: JSBI
  ): Promise<[JettonAmount<Jetton>, Pool]> {
    invariant(this.involvesToken(inputAmount.jetton), 'TOKEN');

    const zeroForOne = inputAmount.jetton.equals(this.jetton0);

    const {
      amountCalculated: outputAmount,
      sqrtRatioX96,
      liquidity,
      tickCurrent,
    } = await this.swap(zeroForOne, inputAmount.quotient, sqrtPriceLimitX96);
    const outputToken = zeroForOne ? this.jetton1 : this.jetton0;
    return [
      JettonAmount.fromRawAmount(
        outputToken,
        JSBI.multiply(outputAmount, NEGATIVE_ONE)
      ),
      new Pool(
        this.jetton0,
        this.jetton1,
        this.fee,
        sqrtRatioX96,
        liquidity,
        tickCurrent,
        this.tickSpacing,
        this.tickDataProvider
      ),
    ];
  }

  /**
   * Given a desired output amount of a token, return the computed input amount and a pool with state updated after the trade
   * @param outputAmount the output amount for which to quote the input amount
   * @param sqrtPriceLimitX96 The Q64.96 sqrt price limit. If zero for one, the price cannot be less than this value after the swap. If one for zero, the price cannot be greater than this value after the swap
   * @returns The input amount and the pool with updated state
   */
  public async getInputAmount(
    outputAmount: JettonAmount<Jetton>,
    sqrtPriceLimitX96?: JSBI
  ): Promise<[JettonAmount<Jetton>, Pool]> {
    invariant(this.involvesToken(outputAmount.jetton), 'TOKEN');

    const zeroForOne = outputAmount.jetton.equals(this.jetton1);

    const {
      amountCalculated: inputAmount,
      sqrtRatioX96,
      liquidity,
      tickCurrent,
    } = await this.swap(
      zeroForOne,
      JSBI.multiply(outputAmount.quotient, NEGATIVE_ONE),
      sqrtPriceLimitX96
    );
    const inputToken = zeroForOne ? this.jetton0 : this.jetton1;
    return [
      JettonAmount.fromRawAmount(inputToken, inputAmount),
      new Pool(
        this.jetton0,
        this.jetton1,
        this.fee,
        sqrtRatioX96,
        liquidity,
        tickCurrent,
        this.tickSpacing,
        this.tickDataProvider
      ),
    ];
  }

  /**
   * Executes a swap
   * @param zeroForOne Whether the amount in is token0 or token1
   * @param amountSpecified The amount of the swap, which implicitly configures the swap as exact input (positive), or exact output (negative)
   * @param sqrtPriceLimitX96 The Q64.96 sqrt price limit. If zero for one, the price cannot be less than this value after the swap. If one for zero, the price cannot be greater than this value after the swap
   * @returns amountCalculated
   * @returns sqrtRatioX96
   * @returns liquidity
   * @returns tickCurrent
   */
  private async swap(
    zeroForOne: boolean,
    amountSpecified: JSBI,
    sqrtPriceLimitX96?: JSBI
  ): Promise<{
    amountCalculated: JSBI;
    sqrtRatioX96: JSBI;
    liquidity: JSBI;
    tickCurrent: number;
  }> {
    if (!sqrtPriceLimitX96)
      sqrtPriceLimitX96 = zeroForOne
        ? JSBI.add(TickMath.MIN_SQRT_RATIO, ONE)
        : JSBI.subtract(TickMath.MAX_SQRT_RATIO, ONE);

    if (zeroForOne) {
      invariant(
        JSBI.greaterThan(sqrtPriceLimitX96, TickMath.MIN_SQRT_RATIO),
        'RATIO_MIN'
      );
      invariant(
        JSBI.lessThan(sqrtPriceLimitX96, this.sqrtRatioX96),
        'RATIO_CURRENT'
      );
    } else {
      invariant(
        JSBI.lessThan(sqrtPriceLimitX96, TickMath.MAX_SQRT_RATIO),
        'RATIO_MAX'
      );
      invariant(
        JSBI.greaterThan(sqrtPriceLimitX96, this.sqrtRatioX96),
        'RATIO_CURRENT'
      );
    }

    const exactInput = JSBI.greaterThanOrEqual(amountSpecified, ZERO);

    // keep track of swap state
    const state = {
      amountSpecifiedRemaining: amountSpecified,
      amountCalculated: ZERO,
      sqrtPriceX96: this.sqrtRatioX96,
      tick: this.tickCurrent,
      liquidity: this.liquidity,
    };

    // Limit maximum iterations to avoid infinite loops
    const MAX_ITERATIONS = 10000;
    let iterations = 0;

    // start swap while loop
    while (
      JSBI.notEqual(state.amountSpecifiedRemaining, ZERO) &&
      state.sqrtPriceX96 !== sqrtPriceLimitX96
    ) {
      if (iterations++ > MAX_ITERATIONS) {
        console.error('Max iterations exceeded in swap loop', {
          state,
          zeroForOne,
          amountSpecified,
          sqrtPriceLimitX96,
        });
        throw new Error('Swap loop exceeded maximum iterations');
      }

      const step: Partial<StepComputations> = {};
      step.sqrtPriceStartX96 = state.sqrtPriceX96;

      // because each iteration of the while loop rounds, we can't optimize this code (relative to the smart contract)
      // by simply traversing to the next available tick, we instead need to exactly replicate
      // tickBitmap.nextInitializedTickWithinOneWord
      [
        step.tickNext,
        step.initialized,
      ] = await this.tickDataProvider.nextInitializedTickWithinOneWord(
        state.tick,
        zeroForOne,
        this.tickSpacing
      );

      if (step.tickNext < TickMath.MIN_TICK) {
        step.tickNext = TickMath.MIN_TICK;
      } else if (step.tickNext > TickMath.MAX_TICK) {
        step.tickNext = TickMath.MAX_TICK;
      }

      step.sqrtPriceNextX96 = TickMath.getSqrtRatioAtTick(step.tickNext);

      [
        state.sqrtPriceX96,
        step.amountIn,
        step.amountOut,
        step.feeAmount,
      ] = SwapMath.computeSwapStep(
        BigInt(state.sqrtPriceX96.toString()),
        (zeroForOne
        ? JSBI.lessThan(step.sqrtPriceNextX96, sqrtPriceLimitX96)
        : JSBI.greaterThan(step.sqrtPriceNextX96, sqrtPriceLimitX96))
          ? BigInt(sqrtPriceLimitX96.toString())
          : BigInt(step.sqrtPriceNextX96.toString()),
        BigInt(state.liquidity.toString()),
        BigInt(state.amountSpecifiedRemaining.toString()),
        BigInt(this.fee)
      );

      if (exactInput) {
        state.amountSpecifiedRemaining = JSBI.subtract(
          state.amountSpecifiedRemaining,
          JSBI.add(step.amountIn, step.feeAmount)
        );
        state.amountCalculated = JSBI.subtract(
          state.amountCalculated,
          step.amountOut
        );
      } else {
        state.amountSpecifiedRemaining = JSBI.add(
          state.amountSpecifiedRemaining,
          step.amountOut
        );
        state.amountCalculated = JSBI.add(
          state.amountCalculated,
          JSBI.add(step.amountIn, step.feeAmount)
        );
      }

      if (JSBI.equal(state.sqrtPriceX96, step.sqrtPriceNextX96)) {
        // if the tick is initialized, run the tick transition
        if (step.initialized) {
          let liquidityNet = JSBI.BigInt(
            (await this.tickDataProvider.getTick(step.tickNext)).liquidityNet
          );
          // if we're moving leftward, we interpret liquidityNet as the opposite sign
          if (zeroForOne)
            liquidityNet = JSBI.multiply(liquidityNet, NEGATIVE_ONE);

          state.liquidity = LiquidityMath.addDelta(
            state.liquidity,
            liquidityNet
          );
        }

        state.tick = zeroForOne ? step.tickNext - 1 : step.tickNext;
      } else if (JSBI.notEqual(state.sqrtPriceX96, step.sqrtPriceStartX96)) {
        // updated comparison function
        // recompute unless we're on a lower tick boundary (i.e. already transitioned ticks), and haven't moved
        state.tick = TickMath.getTickAtSqrtRatio(state.sqrtPriceX96);
      }
    }

    return {
      amountCalculated: state.amountCalculated,
      sqrtRatioX96: state.sqrtPriceX96,
      liquidity: state.liquidity,
      tickCurrent: state.tick,
    };
  }
}
